// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Subscription Badge
// apps/web/src/components/SubscriptionBadge.tsx
//
// Sidebar widget showing the current tenant's subscription tier with a
// colored pill and an action button (Upgrade for Free, Manage for paid).
// Renders nothing for non-Owner/Admin users (the API returns 403 for them).
// ═══════════════════════════════════════════════════════════════════════════

import { useNavigate } from '@tanstack/react-router';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from './engine/tokens';
import { useSubscription, normalizeTier, type SubscriptionTier } from '../lib/api/billing';

// Tier → color mapping. Order matches the PlanMatrix in TenantProvisioningService.cs.
// Values must match SubscriptionTier in billing.ts — the server stores one of
// exactly these strings in Tenant.SubscriptionTier (post-2026.4.9 fix).
const TIER_COLORS: Record<SubscriptionTier, string> = {
  Free:         C.dim,
  Builder:      C.cyan,
  Professional: C.violet,
  Enterprise:   C.amber,
};

const TIER_LABELS: Record<SubscriptionTier, string> = {
  Free:         'FREE',
  Builder:      'BUILDER',
  Professional: 'PROFESSIONAL',
  Enterprise:   'ENTERPRISE',
};

export function SubscriptionBadge() {
  const { data, loading } = useSubscription();
  const navigate          = useNavigate();

  // Hide for non-Owner/Admin users (the hook returns null on 403)
  // and during the first load to avoid layout shimmer.
  if (loading || !data) return null;

  const tier        = normalizeTier(data.tier);
  const color       = TIER_COLORS[tier];
  const label       = TIER_LABELS[tier];
  const isFree      = tier === 'Free';
  const buttonLabel = isFree ? 'Upgrade' : 'Manage';

  return (
    <div
      style={{
        background:    `linear-gradient(135deg, ${color}10, transparent)`,
        border:        `1px solid ${color}30`,
        borderRadius:  10,
        padding:       '12px 14px',
        display:       'flex',
        flexDirection: 'column',
        gap:           10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div
          style={{
            fontSize:      9,
            fontFamily:    F.mono,
            letterSpacing: '0.14em',
            color:         C.muted,
            textTransform: 'uppercase',
          }}
        >
          Plan
        </div>
        <div
          style={{
            fontSize:      10,
            fontFamily:    F.mono,
            fontWeight:    700,
            letterSpacing: '0.10em',
            color,
            background:    `${color}18`,
            border:        `1px solid ${color}40`,
            padding:       '3px 8px',
            borderRadius:  6,
          }}
        >
          {label}
        </div>
      </div>

      <button
        type="button"
        onClick={() => navigate({ to: '/billing' })}
        style={{
          width:        '100%',
          background:   isFree ? color : 'transparent',
          color:        isFree ? '#0a0b14' : color,
          border:       isFree ? 'none' : `1px solid ${color}50`,
          borderRadius: 6,
          padding:      '8px 12px',
          fontSize:     12,
          fontFamily:   F.display,
          fontWeight:   600,
          cursor:       'pointer',
          letterSpacing: '0.02em',
          transition:   'all 0.15s',
          boxShadow:    isFree ? `0 4px 14px -4px ${color}80` : 'none',
        }}
        onMouseEnter={(e) => {
          if (!isFree) {
            e.currentTarget.style.background = `${color}15`;
          } else {
            e.currentTarget.style.transform  = 'translateY(-1px)';
            e.currentTarget.style.boxShadow  = `0 6px 18px -4px ${color}A0`;
          }
        }}
        onMouseLeave={(e) => {
          if (!isFree) {
            e.currentTarget.style.background = 'transparent';
          } else {
            e.currentTarget.style.transform  = 'translateY(0)';
            e.currentTarget.style.boxShadow  = `0 4px 14px -4px ${color}80`;
          }
        }}
      >
        {buttonLabel} →
      </button>
    </div>
  );
}
